import React, { useMemo } from "react";
import _ from "lodash";

import theme from "theme";
import { formatNumberToString, isNotUndefined, useUuid } from "utils/common";

import Box from "components/Box";

import { ProformaHeaderTypeTotal } from "entities/Proforma/components/ProformaContentHeader";
import ProformaPaper from "entities/Proforma/components/ProformaPaper";
import ProformaTable from "entities/Proforma/components/ProformaTable";
import { IProforma } from "entities/Proforma/sdk";

export interface IProformaHardCosts {
  proforma: IProforma;
}

const headerHardCosts: Array<{ key: string; name: string }> = [
  { key: "costs", name: "costs" },
  { key: "total", name: "total" },
  { key: "perunit", name: "per unit" },
  { key: "persqf", name: "per sqf" },
  { key: "percentgroup", name: "% group" },
  { key: "percenttotal", name: "% total" },
];

const ProformaHardCosts: React.FC<IProformaHardCosts> = ({ proforma }) => {
  const uuid = useUuid();
  const tradeCostsDetails = useMemo(
    () =>
      _.orderBy(
        _.map(proforma.budget_data.hard_cost, (value, key) => {
          if (value.value !== 0)
            return {
              costs: key,
              total: formatNumberToString({ number: value.value }),
              perunit: formatNumberToString({
                number: value.value / proforma.core_data.total_units,
              }),
              persqf: formatNumberToString({
                number: value.value / proforma.core_data.total_s_f,
              }),
              percentgroup: `${formatNumberToString({
                number: value.percentage_of_group,
              })}%`,
              percenttotal: `${formatNumberToString({
                number: value.percentage_of_total,
              })}%`,
              position: value.position,
            };
        }).filter(isNotUndefined),
        "position"
      ),
    [proforma]
  );

  const hardCostsRows = useMemo(
    () => [
      {
        costs: "Trade Costs",
        total: formatNumberToString({
          number: proforma.budget_data.total_trade_cost_breakdown.value,
        }),
        perunit: formatNumberToString({
          number:
            proforma.budget_data.total_trade_cost_breakdown.value /
            proforma.core_data.total_units,
        }),
        persqf: formatNumberToString({
          number:
            proforma.budget_data.total_trade_cost_breakdown.value /
            proforma.core_data.total_s_f,
        }),
        percentgroup: `${formatNumberToString({
          number:
            proforma.budget_data.total_trade_cost_breakdown.percentage_of_group,
        })}%`,
        percenttotal: `${formatNumberToString({
          number:
            proforma.budget_data.total_trade_cost_breakdown.percentage_of_total,
        })}%`,
        detail: tradeCostsDetails,
      },
      {
        costs: "Miscellaneous Items",
        total: formatNumberToString({
          number: proforma.budget_data.miscellaneous_items.value,
        }),
        perunit: formatNumberToString({
          number:
            proforma.budget_data.miscellaneous_items.value /
            proforma.core_data.total_units,
        }),
        persqf: formatNumberToString({
          number:
            proforma.budget_data.miscellaneous_items.value /
            proforma.core_data.total_s_f,
        }),
        percentgroup: `${formatNumberToString({
          number: proforma.budget_data.miscellaneous_items.percentage_of_group,
        })}%`,
        percenttotal: `${formatNumberToString({
          number: proforma.budget_data.miscellaneous_items.percentage_of_total,
        })}%`,
      },
      {
        costs: "Total Hard Costs",
        total: formatNumberToString({
          number: proforma.budget_data.total_hard_cost.value,
        }),
        perunit: formatNumberToString({
          number:
            proforma.budget_data.total_hard_cost.value /
            proforma.core_data.total_units,
        }),
        persqf: formatNumberToString({
          number:
            proforma.budget_data.total_hard_cost.value /
            proforma.core_data.total_s_f,
        }),
        percentgroup: `${formatNumberToString({
          number: proforma.budget_data.total_hard_cost.percentage_of_group,
        })}%`,
        percenttotal: `${formatNumberToString({
          number: proforma.budget_data.total_hard_cost.percentage_of_total,
        })}%`,
      },
      {
        costs: "Hard Cost Contingency",
        total: formatNumberToString({
          number: proforma.budget_data.hard_cost_contingency.value,
        }),
        perunit: formatNumberToString({
          number:
            proforma.budget_data.hard_cost_contingency.value /
            proforma.core_data.total_units,
        }),
        persqf: formatNumberToString({
          number:
            proforma.budget_data.hard_cost_contingency.value /
            proforma.core_data.total_s_f,
        }),
        percentgroup: `${formatNumberToString({
          number:
            proforma.budget_data.hard_cost_contingency.percentage_of_group,
        })}%`,
        percenttotal: `${formatNumberToString({
          number:
            proforma.budget_data.hard_cost_contingency.percentage_of_total,
        })}%`,
      },
    ],
    [proforma.budget_data, proforma.core_data, tradeCostsDetails]
  );

  const tableTotals = useMemo(
    () => ({
      costs: "Hard Costs w/ Contingency",
      total: formatNumberToString({
        number:
          proforma.budget_data.hard_cost_contingency.value +
          proforma.budget_data.total_hard_cost.value,
      }),
      perunit: formatNumberToString({
        number:
          (proforma.budget_data.hard_cost_contingency.value +
            proforma.budget_data.total_hard_cost.value) /
          proforma.core_data.total_units,
      }),
      persqf: formatNumberToString({
        number:
          (proforma.budget_data.hard_cost_contingency.value +
            proforma.budget_data.total_hard_cost.value) /
          proforma.core_data.total_s_f,
      }),
      percentgroup: `${formatNumberToString({
        number:
          proforma.budget_data.hard_cost_contingency.percentage_of_group +
          proforma.budget_data.total_hard_cost.percentage_of_group,
      })}%`,
      percenttotal: `${formatNumberToString({
        number:
          proforma.budget_data.hard_cost_contingency.percentage_of_total +
          proforma.budget_data.total_hard_cost.percentage_of_total,
      })}%`,
    }),
    [proforma.budget_data, proforma.core_data]
  );

  const subTitle =
    formatNumberToString({
      number:
        proforma.budget_data.hard_cost_contingency.percentage_of_total +
        proforma.budget_data.total_hard_cost.percentage_of_total,
    }) + "% of TDC";

  const totals = useMemo(() => {
    const totalHeader =
      proforma.budget_data.hard_cost_contingency.value +
      proforma.budget_data.total_hard_cost.value;

    return [
      {
        label: "Total",
        value: totalHeader,
        type: ProformaHeaderTypeTotal.PRICE,
      },
      {
        label: "Per Unit",
        value: totalHeader / proforma.core_data.total_units,
        type: ProformaHeaderTypeTotal.PRICE,
      },
      {
        label: "Per Sqf",
        value: totalHeader / proforma.core_data.total_s_f,
        type: ProformaHeaderTypeTotal.PRICE,
      },
    ];
  }, [proforma.budget_data, proforma.core_data]);

  return (
    <ProformaPaper
      title="Hard Costs"
      subTitle={subTitle}
      totals={totals}
      key={uuid}
    >
      <Box
        sx={{
          margin: theme.spacing(3, 0),
        }}
      >
        <ProformaTable
          key={uuid}
          rows={hardCostsRows}
          headers={headerHardCosts}
          totals={tableTotals}
        />
      </Box>
    </ProformaPaper>
  );
};

export default ProformaHardCosts;
